import React, { useState, useEffect } from 'react';
import { Clock, User, Calendar, Check, Loader, RotateCcw, CheckCircle, FileText } from 'lucide-react';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import RecurrenceDeleteModal from './RecurrenceDeleteModal';
import { useTheme } from '../contexts/ThemeContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const AppointmentViewModal = ({ 
  appointment, 
  employees, 
  onClose, 
  onEdit, 
  onDelete, 
  hasPermission 
}) => {
  const { theme } = useTheme();
  const isDarkMode = document.documentElement.classList.contains('dark');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tasks, setTasks] = useState([]);

  const canEditAppointments = hasPermission ? hasPermission({ resource: 'appointments', action: 'write' }) : false;

  // Charger les tâches depuis Firestore
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const tasksCollection = collection(db, 'tasks');
        const taskSnapshot = await getDocs(tasksCollection);
        const taskList = taskSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTasks(taskList);
      } catch (error) {
        console.error("Erreur lors de la récupération des tâches:", error);
      }
    };

    if (appointment?.status === 'completed') {
      fetchTasks();
    }
  }, [appointment?.status]);

  // Fonction pour gérer la suppression
  const handleDeleteClick = () => {
    if (appointment.isRecurring && appointment.recurrenceSeriesId) {
      // Afficher le modal de confirmation pour les rendez-vous récurrents
      setShowDeleteModal(true);
    } else {
      // Suppression directe pour les rendez-vous non récurrents
      onDelete(appointment.id);
    }
  };

  // Fonction pour confirmer la suppression récurrente
  const handleRecurrenceDeleteConfirm = (scope) => {
    // Passer le scope de suppression à la fonction parent
    onDelete(appointment.id, scope);
    setShowDeleteModal(false);
  };

  // Fonction pour obtenir le nom de l'employé
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Non assigné';
  };

  // Fonction pour obtenir le statut en français
  const getStatusText = (status) => {
    switch (status) {
      case 'assigne': return 'Assigné';
      case 'libre': return 'À combler';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      default: return status;
    }
  };

  // Fonction pour obtenir l'icône de statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return <Loader size={16} className="animate-spin text-blue-500" />;
      case 'completed':
        return <Check size={16} className="text-green-500" />;
      default:
        return null;
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'assigne':
        return isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800';
      case 'libre':
        return isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'completed':
        return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
      default:
        return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir le nom du jour de la semaine
  const getDayOfWeekName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day];
  };

  // Fonction pour obtenir le nom de la semaine du mois
  const getWeekOfMonthName = (week) => {
    const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
    return weeks[Math.min(week - 1, 4)];
  };

  if (!appointment) return null;

  return (
    <EnhancedModal onClose={onClose} size="lg">
      <ModalHeader>
        Détails du rendez-vous
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          {/* Informations principales */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Informations principales
            </h3>
            
            <div className="space-y-4">
              {/* Bénéficiaire */}
              <div className="flex items-center space-x-3">
                <User className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={18} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bénéficiaire</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {appointment.clientName || 'Bénéficiaire non spécifié'}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center space-x-3">
                <Calendar className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={18} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {appointment.date.toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>

              {/* Horaires */}
              <div className="flex items-center space-x-3">
                <Clock className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={18} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Horaires</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {appointment.startTime} - {appointment.endTime}
                  </p>
                </div>
              </div>

              {/* Intervenant */}
              <div className="flex items-center space-x-3">
                <User className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={18} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Intervenant</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {getEmployeeName(appointment.employeeId)}
                  </p>
                </div>
              </div>

              {/* Statut */}
              <div className="flex items-center space-x-3">
                <div className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {getStatusIcon(appointment.status)}
                </div>
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Statut</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                    {getStatusText(appointment.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Détails supplémentaires */}
          {appointment.details && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Détails supplémentaires
              </h3>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                {appointment.details}
              </p>
            </div>
          )}

          {/* Informations de récurrence */}
          {appointment.isRecurring && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center space-x-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                <RotateCcw size={20} className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                <span>Récurrence</span>
              </h3>
              
              <div className="space-y-2">
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                  <span className="font-medium">Type:</span> {appointment.recurrenceBase === 'daily' ? 'Journalière' : 
                                                            appointment.recurrenceBase === 'weekly' ? 'Hebdomadaire' : 'Mensuelle'}
                </p>
                
                {appointment.recurrenceBase === 'weekly' && (
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    <span className="font-medium">Jours:</span> {appointment.recurrenceDay.map(day => getDayOfWeekName(day)).join(', ')}
                  </p>
                )}
                
                {appointment.recurrenceBase === 'monthly' && appointment.recurrenceMonthType === 'dayOfWeek' && (
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    <span className="font-medium">Répétition:</span> Le {getWeekOfMonthName(appointment.recurrenceMonthWeek)} {getDayOfWeekName(appointment.recurrenceMonthWeekday)} de chaque mois
                  </p>
                )}
                
                {appointment.recurrenceEndType !== 'never' && (
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    <span className="font-medium">Fin:</span> {appointment.recurrenceEndType === 'after' 
                      ? `Après ${appointment.recurrenceEndAfter} occurrences`
                      : `Le ${appointment.recurrenceEndDate ? new Date(appointment.recurrenceEndDate).toLocaleDateString('fr-FR') : 'Date non définie'}`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {appointment.notes && appointment.notes.content && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                📝 Notes
              </h3>
              <p className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>
                {appointment.notes.content}
              </p>
            </div>
          )}

          {/* Section pour les rendez-vous terminés */}
          {appointment.status === 'completed' && (
            <>
              {/* Section des tâches accomplies */}
              {appointment.completedTasks && appointment.completedTasks.length > 0 && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-3 flex items-center space-x-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    <CheckCircle size={20} className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                    <span>Tâches accomplies</span>
                  </h3>

                  <div className="space-y-2">
                    {(() => {
                      const completedTaskIds = appointment.completedTasks || [];
                      const completedTaskObjects = tasks.filter(task => completedTaskIds.includes(task.id));

                      if (completedTaskObjects.length === 0) {
                        return (
                          <div className="text-center py-3">
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Aucune tâche accomplie enregistrée
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {completedTaskObjects.map((task) => (
                            <div
                              key={task.id}
                              className={`flex items-center space-x-2 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}
                            >
                              <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                              <span className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                                {task.name || task.title || 'Tâche sans nom'}
                              </span>
                            </div>
                          ))}
                          <div className={`flex items-center justify-between pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Total accompli :
                            </span>
                            <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                              {completedTaskObjects.length} tâche{completedTaskObjects.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Section des informations de pointage */}
              {appointment.checkInStatus && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-3 flex items-center space-x-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    <Clock size={20} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
                    <span>Informations de pointage</span>
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {appointment.checkInStatus.checkInMethod && (
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Méthode :
                        </p>
                        <p className={`capitalize ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {appointment.checkInStatus.checkInMethod}
                        </p>
                      </div>
                    )}

                    {appointment.checkInStatus.checkInDistance && (
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Distance :
                        </p>
                        <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                          {appointment.checkInStatus.checkInDistance.toFixed(2)} mètres
                        </p>
                      </div>
                    )}

                    {appointment.checkInStatus.checkInTime && (
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Heure de pointage :
                        </p>
                        <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                          {appointment.checkInStatus.checkInTime.toDate().toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}

                    {appointment.checkInStatus.checkOutTime && (
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Heure de clôture :
                        </p>
                        <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                          {appointment.checkInStatus.checkOutTime.toDate().toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Notes de pointage si différentes des notes principales */}
                  {appointment.checkInStatus.notes && appointment.checkInStatus.notes !== appointment.notes?.content && (
                    <div className="mt-4">
                      <div className={`flex items-center space-x-2 mb-2`}>
                        <FileText size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Notes de clôture :
                        </p>
                      </div>
                      <p className={`p-3 rounded ${isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-white text-gray-900'}`}>
                        {appointment.checkInStatus.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-between w-full">
          <ModalButton variant="secondary" onClick={onClose}>
            Fermer
          </ModalButton>
          {canEditAppointments && appointment.status !== 'completed' && (
            <div className="flex space-x-2">
              <ModalButton 
                variant="danger" 
                onClick={handleDeleteClick}
              >
                Supprimer
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={onEdit}
              >
                Modifier
              </ModalButton>
            </div>
          )}
          {canEditAppointments && appointment.status === 'completed' && (
            <div className="flex space-x-2">
              <ModalButton 
                variant="danger" 
                onClick={handleDeleteClick}
              >
                Supprimer
              </ModalButton>
            </div>
          )}
        </div>
      </ModalFooter>

      {/* Modal de confirmation pour la suppression récurrente */}
      <RecurrenceDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleRecurrenceDeleteConfirm}
        appointment={appointment}
      />
    </EnhancedModal>
  );
};

export default AppointmentViewModal;
